import React from 'react';
import { RefreshCw } from 'lucide-react';
import { BackupConfigProps } from './types';
import GitHubConnectModal from './GitHubConnectModal';
import GeneralSettings from './GeneralSettings';
import StorageDriverSelector from './StorageDriverSelector';
import NFSConfigForm from './NFSConfigForm';
import { useConfigValidation } from './hooks/useConfigValidation';

import S3ConfigForm from './S3ConfigForm';
import GitHubConfigForm from './GitHubConfigForm';
import SFTPConfigForm from './SFTPConfigForm';
import LocalConfigForm from './LocalConfigForm';

const ConfigPanel: React.FC<BackupConfigProps> = ({
  config,
  loading,
  onConfigChange,
  onSaveConfig,
}) => {
  const [githubModal, setGithubModal] = React.useState(false);
  const { errors, validate } = useConfigValidation(config);

  const handleSave = async () => {
    if (!validate()) return;
    await onSaveConfig();
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-6">
          Backup Configuration
        </h3>

        <GeneralSettings config={config} onConfigChange={onConfigChange} />
        
        <div className="mt-6">
          <StorageDriverSelector 
            config={config} 
            onConfigChange={onConfigChange}
            onOpenGithubConnect={() => setGithubModal(true)}
          />
        </div>

        {/* Driver specific configuration */}
        {config.driver === 'local' && import.meta.env.MODE !== 'production' && (
          <LocalConfigForm config={config} onConfigChange={onConfigChange} />
        )}

        {config.driver === 'github' && (
          <GitHubConfigForm
            config={config}
            onConfigChange={onConfigChange}
            onOpenGithubConnect={() => setGithubModal(true)}
          />
        )}

        {config.driver === 's3' && <S3ConfigForm config={config} onConfigChange={onConfigChange} />}

        {config.driver === 'sftp' && (
          <SFTPConfigForm config={config} onConfigChange={onConfigChange} />
        )}

        {config.driver === 'nfs' && (
          <NFSConfigForm config={config} onConfigChange={onConfigChange} />
        )}

        <div className="flex justify-end mt-8">
          {errors.length > 0 && (
            <div className="text-sm text-red-600 mb-3">
              {errors.map((e, i) => (
                <div key={i}>• {e}</div>
              ))}
            </div>
          )}
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            onClick={handleSave}
            disabled={loading || errors.length > 0}
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin inline" /> : null}
            Save Configuration
          </button>
        </div>
      </div>
      {githubModal && (
        <GitHubConnectModal
          open={githubModal}
          onClose={() => setGithubModal(false)}
          onConnected={(tokenId, owner, repo) => {
            onConfigChange({
              ...config,
              github: { ...(config.github || {}), tokenId, owner, repo },
            });
            setGithubModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ConfigPanel;
